import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import SuperAdminNav from '../components/SuperAdminNav';
const AUDIENCES = [
  { key: 'coach', label: 'Coaches' },
  { key: 'club', label: 'Clubs' },
  { key: 'player', label: 'Players' },
  { key: 'parent', label: 'Parents' },
];

export default function SuperFaq() {
  const { apiFetch } = useAuth();

  const [faqs, setFaqs] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [showCatModal, setShowCatModal] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ question: '', answer: '', audience: [], category_id: '' });
  const [saving, setSaving] = useState(false);
  const [expandedId, setExpandedId] = useState(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState(null);

  // Category modal state
  const [newCatName, setNewCatName] = useState('');
  const [catSaving, setCatSaving] = useState(false);
  const [catDeleteConfirm, setCatDeleteConfirm] = useState(null);
  const [catError, setCatError] = useState('');

  // Add new category inline in FAQ modal
  const [addNewCatChecked, setAddNewCatChecked] = useState(false);
  const [inlineNewCatName, setInlineNewCatName] = useState('');

  const loadFaqs = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/faq');
      setFaqs(data.faqs);
    } catch (err) {
      console.error('Failed to load FAQs:', err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch]);

  const loadCategories = useCallback(async () => {
    try {
      const data = await apiFetch('/api/super/faq-categories');
      setCategories(data.categories);
    } catch (err) {
      console.error('Failed to load categories:', err);
    }
  }, [apiFetch]);

  useEffect(() => { loadFaqs(); loadCategories(); }, [loadFaqs, loadCategories]);

  const toggleAudience = (key) => {
    setForm(f => ({
      ...f,
      audience: f.audience.includes(key)
        ? f.audience.filter(a => a !== key)
        : [...f.audience, key],
    }));
  };

  const openAdd = () => {
    setEditing(null);
    setForm({ question: '', answer: '', audience: [], category_id: '' });
    setAddNewCatChecked(false);
    setInlineNewCatName('');
    setShowModal(true);
  };

  const openEdit = (faq) => {
    setEditing(faq);
    setForm({
      question: faq.question,
      answer: faq.answer,
      audience: Array.isArray(faq.audience) ? [...faq.audience] : [],
      category_id: faq.category_id || '',
    });
    setAddNewCatChecked(false);
    setInlineNewCatName('');
    setShowModal(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (form.audience.length === 0) return;
    setSaving(true);
    try {
      let categoryId = form.category_id;
      // Create inline category if checked
      if (addNewCatChecked && inlineNewCatName.trim()) {
        const catData = await apiFetch('/api/super/faq-categories', {
          method: 'POST',
          body: JSON.stringify({ name: inlineNewCatName.trim() }),
        });
        categoryId = catData.category.id;
        await loadCategories();
      }
      const body = { ...form, category_id: categoryId || null };
      if (editing) {
        await apiFetch(`/api/super/faq/${editing.id}`, { method: 'PUT', body: JSON.stringify(body) });
      } else {
        await apiFetch('/api/super/faq', { method: 'POST', body: JSON.stringify(body) });
      }
      setShowModal(false);
      loadFaqs();
    } catch (err) {
      console.error('Save FAQ error:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    try {
      await apiFetch(`/api/super/faq/${id}`, { method: 'DELETE' });
      setConfirmDeleteId(null);
      loadFaqs();
    } catch (err) {
      console.error('Delete FAQ error:', err);
    }
  };

  // Drag-and-drop for FAQs within an audience+category section
  const handleAudienceDragEnd = async (result, audienceKey) => {
    if (!result.destination || result.source.droppableId !== result.destination.droppableId) return;
    const catId = result.source.droppableId;
    // Get FAQs for this audience + category
    const sectionFaqs = faqs.filter(f =>
      (Array.isArray(f.audience) ? f.audience : []).includes(audienceKey) &&
      (f.category_id || 'uncategorized') === catId
    );
    const reordered = Array.from(sectionFaqs);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    // Build new global order: category by category, with reordered section first
    const seen = new Set();
    const newOrder = [];
    const allCatIds = [...categories.map(c => c.id), 'uncategorized'];
    for (const cid of allCatIds) {
      if (cid === catId) {
        for (const faq of reordered) {
          if (!seen.has(faq.id)) { seen.add(faq.id); newOrder.push(faq); }
        }
      }
      for (const faq of faqs.filter(f => (f.category_id || 'uncategorized') === cid)) {
        if (!seen.has(faq.id)) { seen.add(faq.id); newOrder.push(faq); }
      }
    }
    setFaqs(newOrder);
    try {
      await apiFetch('/api/super/faq/reorder', {
        method: 'PUT',
        body: JSON.stringify({ orderedIds: newOrder.map(f => f.id) }),
      });
    } catch (err) {
      console.error('Reorder failed:', err);
      loadFaqs();
    }
  };

  // Category management
  const handleAddCategory = async () => {
    if (!newCatName.trim()) return;
    setCatSaving(true);
    setCatError('');
    try {
      await apiFetch('/api/super/faq-categories', {
        method: 'POST',
        body: JSON.stringify({ name: newCatName.trim() }),
      });
      setNewCatName('');
      loadCategories();
    } catch (err) {
      setCatError(err.message);
    } finally {
      setCatSaving(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    setCatError('');
    try {
      await apiFetch(`/api/super/faq-categories/${id}`, { method: 'DELETE' });
      setCatDeleteConfirm(null);
      loadCategories();
    } catch (err) {
      setCatError(err.message);
    }
  };

  const handleCatDragEnd = async (result) => {
    if (!result.destination) return;
    const reordered = Array.from(categories);
    const [moved] = reordered.splice(result.source.index, 1);
    reordered.splice(result.destination.index, 0, moved);
    setCategories(reordered);
    try {
      await apiFetch('/api/super/faq-categories/reorder', {
        method: 'PUT',
        body: JSON.stringify({ orderedIds: reordered.map(c => c.id) }),
      });
    } catch (err) {
      console.error('Reorder categories failed:', err);
      loadCategories();
    }
  };

  const isFaqFormValid = form.audience.length > 0 && form.question && form.answer &&
    (!addNewCatChecked || inlineNewCatName.trim());

  if (loading) return <div className="loading"><div className="spinner" /></div>;

  // Helper to render a single FAQ item row
  const renderFaqItem = (faq, index, provided, snapshot) => (
    <div ref={provided.innerRef} {...provided.draggableProps}
      className={`faq-admin-item ${snapshot.isDragging ? 'dragging' : ''}`}>
      <div className="faq-admin-header">
        <span {...provided.dragHandleProps} className="faq-drag-handle">
          <i className="fas fa-grip-vertical"></i>
        </span>
        <div className="faq-admin-question"
          onClick={() => setExpandedId(expandedId === faq.id ? null : faq.id)}>
          {faq.question}
        </div>
        <div className="faq-admin-actions">
          <button className="btn btn-sm btn-outline" onClick={() => openEdit(faq)}>Edit</button>
          {confirmDeleteId === faq.id ? (
            <>
              <button className="btn btn-sm btn-danger" onClick={() => handleDelete(faq.id)}>Confirm</button>
              <button className="btn btn-sm btn-outline" onClick={() => setConfirmDeleteId(null)}>Cancel</button>
            </>
          ) : (
            <button className="btn btn-sm btn-danger" onClick={() => setConfirmDeleteId(faq.id)}>Delete</button>
          )}
        </div>
      </div>
      {expandedId === faq.id && (
        <div className="faq-admin-answer">
          <p style={{ margin: 0 }}>{faq.answer}</p>
          <div className="faq-audience-tags">
            {(Array.isArray(faq.audience) ? faq.audience : []).map(a => (
              <span key={a} className="faq-audience-tag">
                {AUDIENCES.find(au => au.key === a)?.label || a}
              </span>
            ))}
          </div>
        </div>
      )}
    </div>
  );

  return (
    <div className="admin-layout">
      <SuperAdminNav activeItem="faq" />
      <div className="admin-page">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
          <h1 className="page-title">FAQ Management</h1>
          <div style={{ display: 'flex', gap: 8 }}>
            <button className="btn btn-sm" style={{ background: 'var(--blue)', color: '#fff', border: 'none' }} onClick={() => setShowCatModal(true)}>Manage Categories</button>
            <button className="btn btn-sm btn-orange" onClick={openAdd}>+ Add FAQ</button>
          </div>
        </div>

        {faqs.length === 0 ? (
          <p style={{ color: 'var(--text-muted)', textAlign: 'center', marginTop: 32 }}>
            No FAQs yet. Click &quot;Add FAQ&quot; to create one.
          </p>
        ) : (
          AUDIENCES.map(aud => {
            const audFaqs = faqs.filter(f => (Array.isArray(f.audience) ? f.audience : []).includes(aud.key));
            if (audFaqs.length === 0) return null;
            const audUncategorized = audFaqs.filter(f => !f.category_id);
            return (
              <div key={aud.key} style={{ marginBottom: 32 }}>
                <h2 style={{ fontSize: '1.1rem', color: 'var(--blue)', marginBottom: 12, borderBottom: '2px solid var(--blue)', paddingBottom: 4 }}>{aud.label}</h2>
                <DragDropContext onDragEnd={(result) => handleAudienceDragEnd(result, aud.key)}>
                  {categories.map(cat => {
                    const catFaqs = audFaqs.filter(f => f.category_id === cat.id);
                    if (catFaqs.length === 0) return null;
                    return (
                      <div key={cat.id} style={{ marginBottom: 16, marginLeft: 16 }}>
                        <h3 style={{ fontSize: '0.95rem', color: 'var(--orange)', marginBottom: 6 }}>{cat.name}</h3>
                        <Droppable droppableId={cat.id}>
                          {(provided) => (
                            <div {...provided.droppableProps} ref={provided.innerRef}>
                              {catFaqs.map((faq, index) => (
                                <Draggable key={faq.id} draggableId={faq.id} index={index}>
                                  {(prov, snap) => renderFaqItem(faq, index, prov, snap)}
                                </Draggable>
                              ))}
                              {provided.placeholder}
                            </div>
                          )}
                        </Droppable>
                      </div>
                    );
                  })}
                  {audUncategorized.length > 0 && (
                    <div style={{ marginBottom: 16, marginLeft: 16 }}>
                      <h3 style={{ fontSize: '0.95rem', color: 'var(--text-muted)', marginBottom: 6 }}>Uncategorized</h3>
                      <Droppable droppableId="uncategorized">
                        {(provided) => (
                          <div {...provided.droppableProps} ref={provided.innerRef}>
                            {audUncategorized.map((faq, index) => (
                              <Draggable key={faq.id} draggableId={faq.id} index={index}>
                                {(prov, snap) => renderFaqItem(faq, index, prov, snap)}
                              </Draggable>
                            ))}
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  )}
                </DragDropContext>
              </div>
            );
          })
        )}
      </div>

      {/* Add/Edit FAQ Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 540 }}>
            <h2>{editing ? 'Edit' : 'Add'} FAQ</h2>
            <form onSubmit={handleSave}>
              <div className="form-group">
                <label className="form-label">Question *</label>
                <input className="form-input" value={form.question}
                  onChange={e => setForm({ ...form, question: e.target.value })}
                  required placeholder="e.g. How do I reset my password?" />
              </div>
              <div className="form-group">
                <label className="form-label">Answer *</label>
                <textarea className="form-textarea" rows={4} value={form.answer}
                  onChange={e => setForm({ ...form, answer: e.target.value })}
                  required placeholder="Write the answer here..." />
              </div>
              <div className="form-group">
                <label className="form-label">Category *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {categories.map(c => (
                    <label key={c.id} className="form-checkbox">
                      <input type="radio" name="faq-category"
                        checked={form.category_id === c.id && !addNewCatChecked}
                        onChange={() => { setForm({ ...form, category_id: c.id }); setAddNewCatChecked(false); }} />
                      {c.name}
                    </label>
                  ))}
                  <label className="form-checkbox">
                    <input type="radio" name="faq-category"
                      checked={addNewCatChecked}
                      onChange={() => { setAddNewCatChecked(true); setForm({ ...form, category_id: '' }); }} />
                    Add New Category
                  </label>
                  {addNewCatChecked && (
                    <input className="form-input" value={inlineNewCatName}
                      onChange={e => setInlineNewCatName(e.target.value)}
                      placeholder="New category name" style={{ marginLeft: 24 }} />
                  )}
                </div>
              </div>
              <div className="form-group">
                <label className="form-label">Visible To *</label>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                  {AUDIENCES.map(a => (
                    <label key={a.key} className="form-checkbox">
                      <input type="checkbox" checked={form.audience.includes(a.key)}
                        onChange={() => toggleAudience(a.key)} />
                      {a.label}
                    </label>
                  ))}
                </div>
              </div>
              <div style={{ display: 'flex', gap: 8, marginTop: 16 }}>
                <button type="submit" className="btn btn-orange" disabled={saving || !isFaqFormValid}>
                  {saving ? 'Saving...' : (editing ? 'Update' : 'Add FAQ')}
                </button>
                <button type="button" className="btn btn-outline" onClick={() => setShowModal(false)}>Cancel</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Manage Categories Modal */}
      {showCatModal && (
        <div className="modal-overlay" onClick={() => setShowCatModal(false)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 480 }}>
            <h2>Manage Categories</h2>
            {catError && <div style={{ color: 'var(--danger)', fontSize: '0.85rem', marginBottom: 12 }}>{catError}</div>}
            <div style={{ display: 'flex', gap: 8, marginBottom: 16 }}>
              <input className="form-input" value={newCatName} onChange={e => setNewCatName(e.target.value)}
                placeholder="New category name" style={{ flex: 1 }}
                onKeyDown={e => { if (e.key === 'Enter') { e.preventDefault(); handleAddCategory(); } }} />
              <button className="btn btn-orange" onClick={handleAddCategory} disabled={catSaving || !newCatName.trim()}>
                {catSaving ? 'Adding...' : 'Add'}
              </button>
            </div>
            {categories.length === 0 ? (
              <p style={{ color: 'var(--text-muted)', textAlign: 'center' }}>No categories yet.</p>
            ) : (
              <DragDropContext onDragEnd={handleCatDragEnd}>
                <Droppable droppableId="categories">
                  {(provided) => (
                    <div {...provided.droppableProps} ref={provided.innerRef}>
                      {categories.map((cat, index) => (
                        <Draggable key={cat.id} draggableId={cat.id} index={index}>
                          {(provided, snapshot) => (
                            <div ref={provided.innerRef} {...provided.draggableProps}
                              className={`faq-admin-item ${snapshot.isDragging ? 'dragging' : ''}`}
                              style={{ ...provided.draggableProps.style, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                <span {...provided.dragHandleProps} className="faq-drag-handle">
                                  <i className="fas fa-grip-vertical"></i>
                                </span>
                                <span>{cat.name}</span>
                                <span style={{ color: 'var(--text-muted)', fontSize: '0.8rem' }}>({cat.faq_count} FAQ{cat.faq_count !== 1 ? 's' : ''})</span>
                              </div>
                              <div>
                                {catDeleteConfirm === cat.id ? (
                                  <span style={{ display: 'flex', gap: 4 }}>
                                    <button className="btn btn-sm btn-danger" onClick={() => handleDeleteCategory(cat.id)}>Confirm</button>
                                    <button className="btn btn-sm btn-outline" onClick={() => setCatDeleteConfirm(null)}>Cancel</button>
                                  </span>
                                ) : (
                                  <button className="btn btn-sm btn-danger" onClick={() => setCatDeleteConfirm(cat.id)}>Delete</button>
                                )}
                              </div>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
            )}
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              <button className="btn btn-outline" onClick={() => { setShowCatModal(false); setCatError(''); }}>Close</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
